import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X } from "lucide-react";

export default function AdminBlog() {
  const { authHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const { data } = await api.get("/blog");
    setPosts(data);
  };

  const openNew = () => {
    setEditing({
      slug: "",
      title: "",
      excerpt: "",
      content: "",
      category: "Pool Maintenance",
      tags: [],
      featured_image: "",
      is_published: true,
    });
  };

  const save = async () => {
    setLoading(true);
    try {
      const body = { ...editing };
      if (posts.find((p) => p.slug === editing.slug)) {
        await api.put(`/blog/${editing.slug}`, body, { headers: authHeaders() });
      } else {
        await api.post("/blog", body, { headers: authHeaders() });
      }
      toast.success("Saved!");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Error saving post");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (slug) => {
    if (!confirm("Delete this post?")) return;
    await api.delete(`/blog/${slug}`, { headers: authHeaders() });
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-extrabold">Blog Posts</h1>
        <Button onClick={openNew} className="bg-brand-pink hover:bg-brand-pink-hover" data-testid="new-post">
          <Plus className="w-4 h-4 mr-1" /> New Post
        </Button>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold">{editing.slug && posts.find((p) => p.slug === editing.slug) ? "Edit" : "New"} Post</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-bold block mb-1">Title</label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") })} data-testid="post-title" />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Slug</label>
                <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="post-slug" />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Category</label>
                <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="post-category" />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Featured Image URL</label>
                <Input value={editing.featured_image} onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} data-testid="post-image" />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Excerpt</label>
                <Textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} data-testid="post-excerpt" />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Content (Markdown supported)</label>
                <Textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} rows={12} data-testid="post-content" />
              </div>
              <div>
                <label className="text-sm font-bold block mb-1">Tags (comma separated)</label>
                <Input
                  value={Array.isArray(editing.tags) ? editing.tags.join(", ") : ""}
                  onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                  data-testid="post-tags"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.is_published}
                  onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                  id="published"
                />
                <label htmlFor="published" className="text-sm font-bold">Published</label>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={loading} className="bg-brand-pink hover:bg-brand-pink-hover" data-testid="save-post">
                  {loading ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 divide-y">
        {posts.map((p) => (
          <div key={p.slug} className="p-5 flex items-center gap-4" data-testid={`admin-post-${p.slug}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 bg-pink-50 text-brand-pink text-xs font-bold rounded">{p.category}</span>
                {!p.is_published && <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-bold rounded">Draft</span>}
              </div>
              <div className="font-bold text-brand-navy">{p.title}</div>
              <div className="text-xs text-slate-500">{p.slug}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setEditing(p)} data-testid={`edit-${p.slug}`}>
              <Edit className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => remove(p.slug)} data-testid={`delete-${p.slug}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
